/**
 * WebSocket protocol shared by the browser client, the API, and the session
 * runner (ARCHITECTURE.md section 9).
 *
 * Every frame is a JSON envelope: { type, sessionId?, payload, ts }. This module
 * defines the envelope, every message in each direction as a zod schema, the
 * inferred TS types, and type guards.
 */
import { z } from 'zod';
import { MESSAGE_SENDERS, NOTIFICATION_KINDS, PUBLISH_STATES, SESSION_MODES, SESSION_STATUSES, } from './constants.js';
import { clickRefSchema } from './click-to-edit.js';
import { TASK_STATES } from './project-md.js';
export const wsEnvelopeSchema = z.object({
    type: z.string(),
    sessionId: z.string().optional(),
    payload: z.unknown(),
    ts: z.string(),
});
export function isEnvelope(value) {
    return wsEnvelopeSchema.safeParse(value).success;
}
/** Build an envelope, stamping the current time. Omits sessionId when not given. */
export function makeEnvelope(type, payload, sessionId) {
    const ts = new Date().toISOString();
    return sessionId === undefined ? { type, payload, ts } : { type, sessionId, payload, ts };
}
/* -------------------------------------------------------------------------- */
/* Local enum + shared schemas                                                */
/* -------------------------------------------------------------------------- */
const sessionModeSchema = z.enum(SESSION_MODES);
const sessionStatusSchema = z.enum(SESSION_STATUSES);
const messageSenderSchema = z.enum(MESSAGE_SENDERS);
const publishStateSchema = z.enum(PUBLISH_STATES);
const notificationKindSchema = z.enum(NOTIFICATION_KINDS);
export const taskStateSchema = z.enum(TASK_STATES);
export const taskItemSchema = z.object({
    index: z.number().int().nonnegative(),
    state: taskStateSchema,
    caption: z.string(),
});
/* -------------------------------------------------------------------------- */
/* Message type name constants                                                */
/* -------------------------------------------------------------------------- */
export const CLIENT_MSG = {
    sessionSubscribe: 'session:subscribe',
    sessionWatch: 'session:watch',
    chatSend: 'chat:send',
    modeSet: 'mode:set',
    chatOnlySet: 'chatonly:set',
    checkpointDone: 'checkpoint:done',
    fixRequest: 'fix:request',
    restoreTask: 'restore:task',
    runStart: 'run:start',
    runPause: 'run:pause',
};
export const SERVER_MSG = {
    agentStream: 'agent:stream',
    agentMessage: 'agent:message',
    tasksUpdate: 'tasks:update',
    sessionStatus: 'session:status',
    previewError: 'preview:error',
    usageLimit: 'usage:limit',
    checkpointPrompt: 'checkpoint:prompt',
    publishStatus: 'publish:status',
    notify: 'notify',
    runStatus: 'run:status',
};
export const RUNNER_MSG = {
    stream: 'runner:stream',
    message: 'runner:message',
    tasks: 'runner:tasks',
    checkpoint: 'runner:checkpoint',
    commit: 'runner:commit',
    error: 'runner:error',
};
/* -------------------------------------------------------------------------- */
/* Payload schemas                                                            */
/* -------------------------------------------------------------------------- */
// Client -> API
export const sessionSubscribePayloadSchema = z.object({ mode: sessionModeSchema.optional() });
export const sessionWatchPayloadSchema = z.object({});
export const chatSendPayloadSchema = z.object({
    text: z.string(),
    attachmentIds: z.array(z.string().uuid()).optional(),
    clickContext: clickRefSchema.optional(),
});
export const modeSetPayloadSchema = z.object({ mode: sessionModeSchema });
export const chatOnlySetPayloadSchema = z.object({ on: z.boolean() });
export const checkpointDonePayloadSchema = z.object({ checkpointId: z.string() });
export const fixRequestPayloadSchema = z.object({ error: z.string() });
export const restoreTaskPayloadSchema = z.object({ taskLine: z.string() });
/** Kick off a Big Build autonomous run for a session. */
export const runStartPayloadSchema = z.object({ sessionId: z.string() });
/** Pause an in-progress autonomous run. */
export const runPausePayloadSchema = z.object({ sessionId: z.string() });
// API -> Client
export const agentStreamPayloadSchema = z.object({ messageId: z.string(), delta: z.string() });
export const agentMessagePayloadSchema = z.object({
    id: z.string(),
    sender: messageSenderSchema,
    caption: z.string(),
    explanation: z.string().nullish(),
    codeRef: z.string().nullish(),
    /** Named worker that produced this message (e.g. "Athena"); absent for the orchestrator. */
    agentName: z.string().nullish(),
});
export const tasksUpdatePayloadSchema = z.object({ tasks: z.array(taskItemSchema) });
export const sessionStatusPayloadSchema = z.object({
    status: sessionStatusSchema,
    previewUrl: z.string().optional(),
});
export const previewErrorPayloadSchema = z.object({ friendly: z.string(), raw: z.string() });
export const usageLimitPayloadSchema = z.object({ kind: z.string(), resetsAt: z.string() });
export const checkpointPromptPayloadSchema = z.object({ id: z.string(), prompt: z.string() });
export const publishStatusPayloadSchema = z.object({
    projectId: z.string().uuid().optional(),
    state: publishStateSchema,
    url: z.string().optional(),
    message: z.string().optional(),
});
export const notifyPayloadSchema = z.object({
    kind: notificationKindSchema,
    body: z.string(),
    href: z.string().optional(),
});
/** Progress of a Big Build autonomous run. */
export const runStatusPayloadSchema = z.object({
    sessionId: z.string(),
    state: z.enum(['running', 'paused_limit', 'done']),
    tasksDone: z.number().int().nonnegative(),
    tasksTotal: z.number().int().nonnegative(),
});
// Runner -> API
export const runnerStreamPayloadSchema = z.object({
    messageId: z.string(),
    delta: z.string(),
    /** Named worker producing this stream; absent for the orchestrator. */
    agentName: z.string().optional(),
});
export const runnerMessagePayloadSchema = z.object({
    caption: z.string(),
    explanation: z.string().nullish(),
    codeRef: z.string().nullish(),
    sender: z.enum(['agent', 'system']).optional(),
    /** Named worker producing this message; absent for the orchestrator. */
    agentName: z.string().optional(),
});
export const runnerTasksPayloadSchema = z.object({ tasks: z.array(taskItemSchema) });
export const runnerCheckpointPayloadSchema = z.object({
    id: z.string().optional(),
    prompt: z.string(),
    expectedAction: z.string(),
});
export const runnerCommitPayloadSchema = z.object({ taskCaption: z.string() });
export const runnerErrorPayloadSchema = z.object({ friendly: z.string(), raw: z.string() });
/* -------------------------------------------------------------------------- */
/* Full message schemas                                                       */
/* -------------------------------------------------------------------------- */
function msg(type, payload) {
    return z.object({
        type: z.literal(type),
        sessionId: z.string().optional(),
        payload,
        ts: z.string(),
    });
}
// Client -> API
export const sessionSubscribeMessageSchema = msg(CLIENT_MSG.sessionSubscribe, sessionSubscribePayloadSchema);
export const sessionWatchMessageSchema = msg(CLIENT_MSG.sessionWatch, sessionWatchPayloadSchema);
export const chatSendMessageSchema = msg(CLIENT_MSG.chatSend, chatSendPayloadSchema);
export const modeSetMessageSchema = msg(CLIENT_MSG.modeSet, modeSetPayloadSchema);
export const chatOnlySetMessageSchema = msg(CLIENT_MSG.chatOnlySet, chatOnlySetPayloadSchema);
export const checkpointDoneMessageSchema = msg(CLIENT_MSG.checkpointDone, checkpointDonePayloadSchema);
export const fixRequestMessageSchema = msg(CLIENT_MSG.fixRequest, fixRequestPayloadSchema);
export const restoreTaskMessageSchema = msg(CLIENT_MSG.restoreTask, restoreTaskPayloadSchema);
export const runStartMessageSchema = msg(CLIENT_MSG.runStart, runStartPayloadSchema);
export const runPauseMessageSchema = msg(CLIENT_MSG.runPause, runPausePayloadSchema);
// API -> Client
export const agentStreamMessageSchema = msg(SERVER_MSG.agentStream, agentStreamPayloadSchema);
export const agentMessageMessageSchema = msg(SERVER_MSG.agentMessage, agentMessagePayloadSchema);
export const tasksUpdateMessageSchema = msg(SERVER_MSG.tasksUpdate, tasksUpdatePayloadSchema);
export const sessionStatusMessageSchema = msg(SERVER_MSG.sessionStatus, sessionStatusPayloadSchema);
export const previewErrorMessageSchema = msg(SERVER_MSG.previewError, previewErrorPayloadSchema);
export const usageLimitMessageSchema = msg(SERVER_MSG.usageLimit, usageLimitPayloadSchema);
export const checkpointPromptMessageSchema = msg(SERVER_MSG.checkpointPrompt, checkpointPromptPayloadSchema);
export const publishStatusMessageSchema = msg(SERVER_MSG.publishStatus, publishStatusPayloadSchema);
export const notifyMessageSchema = msg(SERVER_MSG.notify, notifyPayloadSchema);
export const runStatusMessageSchema = msg(SERVER_MSG.runStatus, runStatusPayloadSchema);
// Runner -> API
export const runnerStreamMessageSchema = msg(RUNNER_MSG.stream, runnerStreamPayloadSchema);
export const runnerMessageMessageSchema = msg(RUNNER_MSG.message, runnerMessagePayloadSchema);
export const runnerTasksMessageSchema = msg(RUNNER_MSG.tasks, runnerTasksPayloadSchema);
export const runnerCheckpointMessageSchema = msg(RUNNER_MSG.checkpoint, runnerCheckpointPayloadSchema);
export const runnerCommitMessageSchema = msg(RUNNER_MSG.commit, runnerCommitPayloadSchema);
export const runnerErrorMessageSchema = msg(RUNNER_MSG.error, runnerErrorPayloadSchema);
export const clientToApiSchema = z.discriminatedUnion('type', [
    sessionSubscribeMessageSchema,
    sessionWatchMessageSchema,
    chatSendMessageSchema,
    modeSetMessageSchema,
    chatOnlySetMessageSchema,
    checkpointDoneMessageSchema,
    fixRequestMessageSchema,
    restoreTaskMessageSchema,
    runStartMessageSchema,
    runPauseMessageSchema,
]);
export const apiToClientSchema = z.discriminatedUnion('type', [
    agentStreamMessageSchema,
    agentMessageMessageSchema,
    tasksUpdateMessageSchema,
    sessionStatusMessageSchema,
    previewErrorMessageSchema,
    usageLimitMessageSchema,
    checkpointPromptMessageSchema,
    publishStatusMessageSchema,
    notifyMessageSchema,
    runStatusMessageSchema,
]);
export const runnerToApiSchema = z.discriminatedUnion('type', [
    runnerStreamMessageSchema,
    runnerMessageMessageSchema,
    runnerTasksMessageSchema,
    runnerCheckpointMessageSchema,
    runnerCommitMessageSchema,
    runnerErrorMessageSchema,
]);
/* -------------------------------------------------------------------------- */
/* Parsers                                                                     */
/* -------------------------------------------------------------------------- */
export function parseClientMessage(value) {
    const r = clientToApiSchema.safeParse(value);
    return r.success ? r.data : null;
}
export function parseApiMessage(value) {
    const r = apiToClientSchema.safeParse(value);
    return r.success ? r.data : null;
}
export function parseRunnerMessage(value) {
    const r = runnerToApiSchema.safeParse(value);
    return r.success ? r.data : null;
}
/* -------------------------------------------------------------------------- */
/* Type guards                                                                */
/* -------------------------------------------------------------------------- */
const matches = (e, type, schema) => e.type === type && schema.safeParse(e.payload).success;
// Client -> API
export const isSessionSubscribe = (e) => matches(e, CLIENT_MSG.sessionSubscribe, sessionSubscribePayloadSchema);
export const isSessionWatch = (e) => matches(e, CLIENT_MSG.sessionWatch, sessionWatchPayloadSchema);
export const isChatSend = (e) => matches(e, CLIENT_MSG.chatSend, chatSendPayloadSchema);
export const isModeSet = (e) => matches(e, CLIENT_MSG.modeSet, modeSetPayloadSchema);
export const isChatOnlySet = (e) => matches(e, CLIENT_MSG.chatOnlySet, chatOnlySetPayloadSchema);
export const isCheckpointDone = (e) => matches(e, CLIENT_MSG.checkpointDone, checkpointDonePayloadSchema);
export const isFixRequest = (e) => matches(e, CLIENT_MSG.fixRequest, fixRequestPayloadSchema);
export const isRestoreTask = (e) => matches(e, CLIENT_MSG.restoreTask, restoreTaskPayloadSchema);
export const isRunStart = (e) => matches(e, CLIENT_MSG.runStart, runStartPayloadSchema);
export const isRunPause = (e) => matches(e, CLIENT_MSG.runPause, runPausePayloadSchema);
// API -> Client
export const isAgentStream = (e) => matches(e, SERVER_MSG.agentStream, agentStreamPayloadSchema);
export const isAgentMessage = (e) => matches(e, SERVER_MSG.agentMessage, agentMessagePayloadSchema);
export const isTasksUpdate = (e) => matches(e, SERVER_MSG.tasksUpdate, tasksUpdatePayloadSchema);
export const isSessionStatus = (e) => matches(e, SERVER_MSG.sessionStatus, sessionStatusPayloadSchema);
export const isPreviewError = (e) => matches(e, SERVER_MSG.previewError, previewErrorPayloadSchema);
export const isUsageLimit = (e) => matches(e, SERVER_MSG.usageLimit, usageLimitPayloadSchema);
export const isCheckpointPrompt = (e) => matches(e, SERVER_MSG.checkpointPrompt, checkpointPromptPayloadSchema);
export const isPublishStatus = (e) => matches(e, SERVER_MSG.publishStatus, publishStatusPayloadSchema);
export const isNotify = (e) => matches(e, SERVER_MSG.notify, notifyPayloadSchema);
export const isRunStatus = (e) => matches(e, SERVER_MSG.runStatus, runStatusPayloadSchema);
// Runner -> API
export const isRunnerStream = (e) => matches(e, RUNNER_MSG.stream, runnerStreamPayloadSchema);
export const isRunnerMessage = (e) => matches(e, RUNNER_MSG.message, runnerMessagePayloadSchema);
export const isRunnerTasks = (e) => matches(e, RUNNER_MSG.tasks, runnerTasksPayloadSchema);
export const isRunnerCheckpoint = (e) => matches(e, RUNNER_MSG.checkpoint, runnerCheckpointPayloadSchema);
export const isRunnerCommit = (e) => matches(e, RUNNER_MSG.commit, runnerCommitPayloadSchema);
export const isRunnerError = (e) => matches(e, RUNNER_MSG.error, runnerErrorPayloadSchema);
//# sourceMappingURL=ws-protocol.js.map